import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ServicesService } from 'app/services/services/services.service';
import { SousServiceService } from 'app/services/sous-Service/sous-service.service';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

interface SousService {
  id: number;
  name: string;
  image_url?: string;
  category?: string;
  status?: string;
  displayOrder?: number;
  orderIndex?: number;
  createdAt?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-list-of-sous-services',
  templateUrl: './list-of-sous-services.component.html',
  styleUrls: ['./list-of-sous-services.component.scss']
})
export class ListOfSousServicesComponent implements OnInit {
  Agency: any;
  list: SousService[] = [];
  idAgency: string;
  isLoading = false;

  constructor(
    private projetsService: ServicesService,
    private sousService: SousServiceService,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idAgency = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
  }

  loadServiceDetails(): void {
    this.isLoading = true;
    this.projetsService.getService(this.idAgency).subscribe({
      next: (data: any) => {
        this.Agency = data;
        const rawList = Array.isArray(data?.sous_services) ? data.sous_services : [];
        this.list = rawList;
        this.initializeOrderIndexes();
        this.isLoading = false;
        console.log('Sous-services chargés:', this.list, data);
      },
      error: (err) => {
        console.error('Erreur chargement service:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de charger les détails du service.',
          icon: 'error'
        });
      }
    });
  }

  private initializeOrderIndexes(): void {
    const hasOrder = this.list.some(s => s.displayOrder !== undefined && s.displayOrder !== null);
    if (hasOrder) {
      // Sort by displayOrder ascending
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      // Fallback to createdAt (oldest first)
      this.list.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    }
    // Assign sequential orderIndex for UI
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  // ============================================================
  // ORDERING METHODS
  // ============================================================

  private saveAllOrders(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    let completed = 0;
    let hasError = false;

    this.list.forEach(sousService => {
      this.sousService.updateSousServiceOrder(sousService.id, sousService.displayOrder!).subscribe({
        next: () => {
          completed++;
          if (completed === this.list.length && !hasError) {
            this.isLoading = false;
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 1500,
              icon: 'success',
              title: 'Ordre mis à jour'
            });
          }
        },
        error: (err) => {
          console.error(`Error updating order for sous-service ${sousService.id}:`, err);
          hasError = true;
          this.isLoading = false;
          Swal.fire({
            title: 'Erreur',
            text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
            icon: 'error'
          });
          this.loadServiceDetails(); // reload to revert
        }
      });
    });
  }

  private updateOrdersAndSave(): void {
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
    this.saveAllOrders();
  }

  drop(event: CdkDragDrop<SousService[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  // ============================================================
  // DELETE
  // ============================================================

  deletesousService(sousService: SousService): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: `Vous êtes sur le point de supprimer "${sousService.name}". Cette action est irréversible !`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.isLoading = true; // Show loading state
        
        this.sousService.deleteSousService(sousService.id).subscribe({
          next: (response) => {
            console.log('Delete successful:', response);
            
            Swal.fire({
              title: 'Supprimé !',
              text: 'Le sous-service a été supprimé avec succès.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false
            });
            
            // REFRESH THE PAGE DATA FROM SERVER
            this.loadServiceDetails();
          },
          error: (err: HttpErrorResponse) => {
            console.error('Erreur suppression:', err);
            this.isLoading = false;
            
            // If it's a 404, it might already be deleted, so we refresh anyway
            if (err.status === 404) {
               this.loadServiceDetails();
               return; 
            }

            Swal.fire({
              title: 'Erreur',
              text: 'La suppression a échoué. Veuillez réessayer.',
              icon: 'error'
            });
          }
        });
      }
    });
  }

  navigateBack(): void {
    this.location.back();
  }
}